/**
 * Reading a heartbeat: pure functions over a timestamp and the interval.
 *
 * Kept out of the component so the judgement — "is this schedule late?" — is
 * unit-testable without rendering anything or faking a clock globally.
 */
import type { PingResponse } from "./types.ts";

/**
 * How much later than the interval a tick may arrive before it counts as late.
 * Half an interval, floored at 30s: a schedule is not overdue the instant it is
 * due (the worker takes a moment, and clocks disagree), but a wholly missed
 * tick must show.
 */
export function graceMs(intervalMs: number): number {
  return Math.max(intervalMs / 2, 30_000);
}

export type HeartbeatHealth = "waiting" | "ok" | "late";

/**
 * Classify the schedule from the last tick.
 *
 *   waiting — nothing has ticked yet (a fresh environment, not a fault)
 *   ok      — the last tick is within the interval plus grace
 *   late    — it is not, so the worker or the schedule is in trouble
 */
export function heartbeatHealth(
  last: PingResponse["heartbeat"]["last"],
  intervalMs: number,
  now: Date,
): HeartbeatHealth {
  if (!last) return "waiting";
  const age = now.getTime() - Date.parse(last.ranAt);
  return age <= intervalMs + graceMs(intervalMs) ? "ok" : "late";
}

/**
 * "just now" / "4 minutes ago" — coarse on purpose. A heartbeat is about
 * *roughly* how stale things are, and a second-by-second label invites reading
 * precision into a number that has network and clock slop in it.
 */
export function agoLabel(iso: string, now: Date): string {
  const seconds = Math.round((now.getTime() - Date.parse(iso)) / 1_000);
  if (seconds < 0) return "just now"; // clock skew; never say "in -3 seconds"
  if (seconds < 45) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** "every 10 minutes" — the cadence the backend reports, in words. */
export function intervalLabel(intervalMs: number): string {
  const seconds = Math.round(intervalMs / 1_000);
  if (seconds < 60) return `every ${seconds} second${seconds === 1 ? "" : "s"}`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `every ${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.round(minutes / 60);
  return `every ${hours} hour${hours === 1 ? "" : "s"}`;
}
